// Producer members data access
import { supabase } from '@/integrations/supabase/client';

export type ProducerMemberRole = 'admin' | 'editor' | 'viewer';

export interface ProducerMember {
  id: string;
  user_id: string;
  role: ProducerMemberRole;
  created_at: string;
  email: string;
}

export interface MyProducerMembership {
  id: string;
  name: string;
  slug: string | null;
  status: string;
  role: ProducerMemberRole;
  coverImageUrl: string | null;
}

/**
 * Fetch all members for a producer (via edge function)
 */
export async function fetchProducerMembers(producerId: string): Promise<ProducerMember[]> {
  const { data: { session } } = await supabase.auth.getSession();
  
  const response = await supabase.functions.invoke('producer-members/list', {
    body: null,
    headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
    method: 'GET',
  });

  // For GET requests, we need to pass query params differently
  // Use fetch directly with the function URL
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/producer-members/list?producerId=${producerId}`;
  
  const fetchResponse = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || ''}`,
      'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
  });

  if (!fetchResponse.ok) {
    const errorData = await fetchResponse.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to fetch members');
  }

  const data = await fetchResponse.json();
  return data.members || [];
}

/**
 * Add a member to a producer by email
 */
export async function addProducerMember(
  producerId: string,
  email: string,
  role: ProducerMemberRole
): Promise<ProducerMember> {
  const response = await supabase.functions.invoke('producer-members/add', {
    body: { producerId, email, role },
  });

  if (response.error) {
    throw new Error(response.error.message || 'Failed to add member');
  }

  if (response.data?.error) {
    throw new Error(response.data.error);
  }

  return response.data.member;
}

/**
 * Remove a member from a producer
 */
export async function removeProducerMember(
  producerId: string,
  userId: string
): Promise<void> {
  const response = await supabase.functions.invoke('producer-members/remove', {
    body: { producerId, userId },
  });

  if (response.error) {
    throw new Error(response.error.message || 'Failed to remove member');
  }

  if (response.data?.error) {
    throw new Error(response.data.error);
  }
}

/**
 * Fetch producers where the current user is a member
 */
export async function fetchMyProducers(): Promise<MyProducerMembership[]> {
  const { data, error } = await supabase
    .from('producer_members')
    .select(`
      role,
      producers:producer_id (
        id,
        name,
        slug,
        status
      )
    `)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching my producers:', error);
    throw error;
  }

  return (data || [])
    .filter((item: any) => item.producers)
    .map((item: any) => ({
      id: item.producers.id,
      name: item.producers.name,
      slug: item.producers.slug,
      status: item.producers.status,
      role: item.role as ProducerMemberRole,
      coverImageUrl: null, // Resolved from media_assets
    }));
}

/**
 * Check if current user has a specific role for a producer
 */
export async function checkProducerRole(
  producerId: string,
  allowedRoles: ProducerMemberRole[] = ['admin']
): Promise<boolean> {
  const { data, error } = await supabase
    .from('producer_members')
    .select('role')
    .eq('producer_id', producerId)
    .single();

  if (error || !data) {
    return false;
  }

  return allowedRoles.includes(data.role as ProducerMemberRole);
}
